import Decimal from "decimal.js";

/**
 * Деньги и проценты.
 *
 * Правило ТЗ №5: фронт ничего не вычисляет. Все суммы, дельты и проценты
 * приходят с бэкенда готовыми строками, здесь они только форматируются.
 * Это единственный модуль, где разрешён `decimal.js`, и единственный, где
 * строка-деньги превращается в число.
 *
 * `Intl.NumberFormat` намеренно не используется: он принимает `number`, а
 * `Number("12345678901234.56")` уже теряет копейки. Разряды режутся строкой.
 */

/** Формат ответа: ровно два знака, `^-?\d+\.\d{2}$`. */
export type Money = string;
/** Проценты с двумя знаками: `earned_pct`, `threshold_pct`, `declared_*`. */
export type Percent2 = string;
/** `DailyResultItem.percent` — три знака: `"-0.800"`. */
export type Percent3 = string;

/** Узкий неразрывный пробел: разряды не разрываются переносом строки. */
const GROUP_SEPARATOR = " ";

export interface FormatMoneyOptions {
  /** `always` ставит «+» у положительных — так в макете размечены P/L. */
  sign?: "auto" | "always";
  /** Суффикс валюты. `null` — без суффикса. */
  suffix?: string | null;
}

export function formatMoney(value: Money, options: FormatMoneyOptions = {}): string {
  return render(new Decimal(value).toFixed(2), options);
}

/**
 * Круглая сумма без хвоста «,00». Два места: цена робота и витринный «общий
 * профит» на карточке каталога. Обе задаются целыми числами, и два нуля на
 * самом заметном числе экрана только шумят — а в строке каталога высотой
 * 122 пикселя ещё и занимают место, которого нет.
 *
 * Ненулевые копейки сохраняются: экран покупки объявляет сумму списания, и
 * расхождение с тем, что спишется на самом деле, читалось бы как ошибка.
 *
 * Отдельная функция, а не флаг в `FormatMoneyOptions`: у экранов не должно
 * быть выбора точности, иначе «целые» разъедутся по местам вызова.
 */
export function formatPrice(value: Money, options: FormatMoneyOptions = {}): string {
  const decimal = new Decimal(value);
  // `isInteger`, а не сравнение строк: контракт допускает и «500», и «500.0»,
  // и «500.00» — хвост у всех трёх одинаково лишний.
  return render(decimal.isInteger() ? decimal.toFixed(0) : decimal.toFixed(2), options);
}

/**
 * Общая часть: знак, разряды, суффикс. На вход — уже готовая десятичная
 * запись, потому что точность выбирает вызывающая функция, а не форматирование.
 */
function render(fixed: string, options: FormatMoneyOptions): string {
  const { sign = "auto", suffix = null } = options;

  const negative = fixed.startsWith("-");
  const digits = negative ? fixed.slice(1) : fixed;
  const [whole = "0", fraction] = digits.split(".");

  const prefix = negative ? "−" : sign === "always" && !isZero(digits) ? "+" : "";
  const body = fraction === undefined ? groupDigits(whole) : `${groupDigits(whole)},${fraction}`;

  return suffix ? `${prefix}${body}${GROUP_SEPARATOR}${suffix}` : `${prefix}${body}`;
}

/**
 * Принимает и двузначные проценты (`earned_pct`), и трёхзначные
 * (`DailyResultItem.percent`) — по умолчанию точность источника сохраняется
 * как есть. Тип здесь `string`, а не `Percent2 | Percent3`: оба псевдонима —
 * это `string`, и union из них не несёт информации для компилятора.
 *
 * `precision` округляет для показа. Нужен там, где процент — подпись рядом с
 * полоской, а не величина: «40,00%» в строке дохода отнимает место у суммы,
 * ради которой строка и существует, а два знака в доле робота ничего не
 * уточняют. Округление живёт здесь, а не на месте вызова: это единственный
 * модуль, которому правило ТЗ №5 разрешает арифметику над деньгами.
 */
export function formatPercent(
  value: string,
  options: { sign?: "auto" | "always"; precision?: number } = {},
): string {
  const { sign = "auto", precision } = options;

  // Знаков может быть 2 или 3 — по умолчанию приводить одно к другому нельзя:
  // бэкенд сознательно отдаёт дневной результат с точностью до тысячных.
  const source =
    precision === undefined
      ? value.trim()
      : new Decimal(value).toFixed(precision, Decimal.ROUND_HALF_UP);

  const negative = source.startsWith("-");
  const digits = negative ? source.slice(1) : source;
  const prefix = negative ? "−" : sign === "always" && !isZero(digits) ? "+" : "";

  return `${prefix}${digits.replace(".", ",")}%`;
}

/** Прочерк вместо числа: показателя нет, и ноль вместо него был бы враньём. */
export const NO_VALUE = "—";

/**
 * Класс цвета по знаку показателя.
 *
 * Раньше каждый экран решал это сам и решал одинаково — жёстким
 * `text-status-profit`. На витрине это было незаметно, пока все демо-роботы
 * стояли в плюсе; отрицательная доходность рисовалась бы зелёной. Ноль
 * нейтральный: «0,00%» зелёным читается как достижение.
 *
 * Это выбор класса по знаку, а не арифметика: правило ТЗ №5 не нарушается.
 */
export function percentTone(value: string | null | undefined): string {
  if (value === null || value === undefined) return "text-text-tertiary";
  const sign = moneySign(value);
  if (sign > 0) return "text-status-profit";
  if (sign < 0) return "text-status-loss";
  return "text-text-primary";
}

/**
 * Знак суммы — для выбора цвета и варианта бейджа.
 * Это сравнение с нулём, а не вычисление: правило ТЗ №5 не нарушается.
 */
export function moneySign(value: Money): -1 | 0 | 1 {
  const decimal = new Decimal(value);
  if (decimal.isZero()) return 0;
  return decimal.isNegative() ? -1 : 1;
}

/**
 * Доля от суммы — для быстрых кнопок 25 % / 50 % / 75 % на экране запуска
 * и пополнения (ТЗ §16, §18).
 *
 * Живёт здесь, а не на странице, потому что это арифметика над деньгами, а
 * правило ТЗ №5 держит её в одном модуле; `rules.test.ts` роняет прогон за
 * `decimal.js` где угодно ещё.
 *
 * Округление ВНИЗ до копейки: подсказка «50 %» не должна предлагать сумму
 * больше того, что реально лежит на балансе, — 50 % от 0.01 обязаны дать
 * 0.00, а не 0.01. Проверку минимума и остатка всё равно делает бэкенд,
 * здесь только удобство ввода.
 */
export function fractionOfMoney(value: Money, percent: number): Money {
  return new Decimal(value)
    .times(percent)
    .dividedBy(100)
    .toFixed(2, Decimal.ROUND_DOWN);
}

/**
 * Сравнение двух денежных строк. Не вычисление: результат — знак, а не сумма.
 *
 * Нужно там, где кнопка обязана погаснуть: доля баланса ниже минимума робота,
 * пополнение больше остатка. Сравнивать строки лексикографически нельзя —
 * «9.00» оказалось бы больше «10.00».
 */
export function compareMoney(left: Money, right: Money): -1 | 0 | 1 {
  return new Decimal(left).comparedTo(right) as -1 | 0 | 1;
}

/**
 * Единственное место в проекте, где деньги становятся числом.
 *
 * Recharts принимает только числа, обойти это нельзя. Поэтому число уходит
 * ТОЛЬКО в геометрию графика: подписи осей и тултипы обязаны рендерить
 * исходную строку через `formatMoney`, иначе потеря точности вылезет в UI.
 */
export function toChartNumber(value: Money): number {
  return new Decimal(value).toNumber();
}

/**
 * Ноль знака не получает даже при `sign: "always"`.
 *
 * «+0,00%» обещает рост там, где не произошло ничего, и ТЗ №2 требует ровно
 * «0,00%». Проверка строкой, а не через `Decimal`: сюда приходит уже готовая
 * десятичная запись без знака, и заводить ради неё разбор числа незачем.
 */
function isZero(digits: string): boolean {
  return /^0(\.0*)?$/.test(digits);
}

function groupDigits(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}
